import { useState, type ReactNode } from "react";
import { AlertTriangle, ChevronDown, ListChecks, Loader2 } from "lucide-react";
import { cn } from "../../lib/cn";
import { microcopy, useDocumentLocale, type UiLocale } from "../../lib/microcopy";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "../collapsible";

export interface WorkStepLike {
  id: string;
  /** What the step did, in plain words ("Read the latest trade figures"). */
  label: string;
  detail?: string | null;
  status: "running" | "done" | "error";
  durationMs?: number | null;
}

/** Text with a light sweeping across it, for "the assistant is busy with this" lines. */
export function Shimmer({ className, children }: { className?: string; children: ReactNode }) {
  return (
    <span className={cn("inline-block bg-[linear-gradient(90deg,var(--color-sand-600)_0%,var(--color-sand-600)_35%,var(--color-aegold-500)_50%,var(--color-sand-600)_65%,var(--color-sand-600)_100%)] bg-[length:250%_100%] bg-clip-text text-transparent animate-shimmer", className)}>
      {children}
    </span>
  );
}

function seconds(ms: number, locale: UiLocale): string {
  return new Intl.NumberFormat(locale === "ar" ? "ar-AE-u-nu-latn" : "en-AE", { style: "unit", unit: "second", unitDisplay: "short", maximumFractionDigits: ms < 10_000 ? 1 : 0 }).format(ms / 1000);
}

/**
 * What the assistant did to answer, folded into one line: while it works the line names the current step; afterwards it reads
 * "Worked through 4 steps · 6 s" and opens to the list.
 */
export function WorkSteps({ steps, running = false, label, summary, defaultOpen = false, className }: {
  steps: WorkStepLike[];
  running?: boolean;
  /** Overrides the line shown while running (defaults to the running step's label). */
  label?: ReactNode;
  /** Overrides the line shown once finished. */
  summary?: ReactNode;
  defaultOpen?: boolean;
  className?: string;
}) {
  const locale = useDocumentLocale();
  const [open, setOpen] = useState(defaultOpen);
  if (steps.length === 0 && !running) return null;
  const current = [...steps].reverse().find((s) => s.status === "running");
  const failed = steps.some((s) => s.status === "error");
  const total = steps.reduce((sum, s) => sum + (s.durationMs ?? 0), 0);
  const headline = running
    ? <Shimmer>{label ?? current?.label ?? microcopy("working", locale)}</Shimmer>
    : summary ?? (steps.length === 1 ? microcopy("workedOneStep", locale) : microcopy("workedSteps", locale, { count: steps.length }));
  return (
    <Collapsible open={open} onOpenChange={setOpen} className={cn("mb-2", className)}>
      <CollapsibleTrigger disabled={steps.length === 0} className="group/steps inline-flex max-w-full items-center gap-2 rounded-lg py-0.5 pe-1.5 text-xs text-sand-700 transition-colors hover:text-aeblack-900 disabled:cursor-default">
        <span className={cn("grid size-5 shrink-0 place-items-center rounded-md", running ? "bg-aegold-50 text-aegold-700" : failed ? "bg-warning-50 text-warning-700" : "bg-sand-100 text-sand-700")}>
          {running ? <Loader2 className="size-3 animate-spin" /> : failed ? <AlertTriangle className="size-3" /> : <ListChecks className="size-3" />}
        </span>
        <span className="min-w-0 truncate font-medium">{headline}</span>
        {!running && total > 0 ? <span className="shrink-0 tabular text-sand-500">· {seconds(total, locale)}</span> : null}
        {steps.length > 0 ? <ChevronDown className="size-3.5 shrink-0 transition-transform group-data-[state=open]/steps:rotate-180" /> : null}
      </CollapsibleTrigger>
      <CollapsibleContent>
        <ol className="ms-2.5 mt-1.5 space-y-2 border-s border-sand-200 py-0.5 ps-4">
          {steps.map((s) => (
            <li key={s.id} className="relative text-xs">
              <span className={cn("absolute -start-[21.5px] top-1 size-2.5 rounded-full ring-2 ring-white", s.status === "error" ? "bg-danger-500" : s.status === "running" ? "animate-pulse bg-aegold-500" : "bg-success-500")} />
              <p className="font-medium text-aeblack-900" dir="auto">
                {s.label}
                {s.status === "error" ? <span className="ms-1.5 font-normal text-danger-700">· {microcopy("stepFailed", locale)}</span> : null}
              </p>
              {s.detail ? <p className="mt-0.5 line-clamp-2 text-muted-foreground" dir="auto" title={s.detail}>{s.detail}</p> : null}
            </li>
          ))}
        </ol>
      </CollapsibleContent>
    </Collapsible>
  );
}
